// Which doors the GATE offers — pure, so the decision is testable without
// rendering (same idiom as gateIdentity.ts and lib/captainAccess.ts).
//
// WHY THIS EXISTS AS A MODULE. The GATE's root screen used to inline its door
// list in JSX, which made its most important behaviour — what happens on match
// night — impossible to assert. The rule below is a product ruling, not a
// styling detail, so it gets a test.
//
// THE MATCH-NIGHT RULE (Luke, 2026-07-26). While a submission window is open
// (from 1h before kickoff until the card closes — shared/submissionWindow.ts),
// the root GATE shows EXACTLY TWO doors: submit your scores, and go to the
// dashboard. Nothing else. The reasoning has two halves and both matter:
//
//   1. FOCUS. During the window the only thing the system is waiting on is
//      screenshots. Offering "Viewing", "Joining", "Apply to create a clan"
//      and "Founder" alongside that buries the one action that unblocks
//      everyone else — and for a clan leader running an all-ghost roster, that
//      one action is twelve to twenty-four uploads nobody else can do.
//
//   2. THE APP MUST STAY REACHABLE. The window is a nudge, never a lock. This
//      is why DASHBOARD is the second door and not, say, a dismissable banner:
//      a gate that funnels you to uploads with no way past is a gate that has
//      taken the product hostage over a screenshot. Two doors — do the thing,
//      or go anywhere else.
//
// Everything outside the window is untouched: with no live window the door list
// below is byte-identical to what the screen rendered before, in the same
// order, so the ordinary GATE cannot regress.

import type { GateIdentity } from './gateIdentity';

export type GateScreenId = 'root' | 'viewing' | 'joining';

/** A door the GATE can offer. `to` is a route; `screen` pushes a sub-screen. */
export interface GateDoor {
  key: string;
  label: string;
  /** Route to navigate to. Mutually exclusive with `screen`. */
  to?: string;
  /** Sub-screen to switch to instead of navigating. */
  screen?: GateScreenId;
  /** Renders in the accent colour — at most one per screen. */
  primary?: boolean;
  /** Small second line under the label. */
  caption?: string;
}

/** Centralised so destinations stay one-line tunable as the flows firm up. */
export const DEST = {
  viewStandings: '/standings',
  viewBracket: '/bracket',
  viewSchedule: '/calendar',
  joinCompAsClan: '/gate/join-comp',   // GATE flow: enter your clan into an open comp
  joinClanAsMember: '/clans/browse',
  buildClan: '/gate/build-clan',       // GATE flow: forge a clan (you become leader)
  buildComp: '/me/org',                // CreateCompetitionPanel lives on My Org
  dashboard: '/',
  founder: '/admin',
} as const;

/** Deep-link to a live match card. Mirrors the backend's own
 *  liveMatchCardPath (shared/liveSubmissionWindow.ts): BOTH ids encoded so
 *  their '#' survives as %23 rather than becoming a fragment marker, and
 *  compId ALWAYS carried — the card page renders "Missing match or
 *  competition id." without it. */
export function matchCardPath(liveMatch: { matchId: string; compId: string }): string {
  return `/matchcard/${encodeURIComponent(liveMatch.matchId)}?compId=${encodeURIComponent(liveMatch.compId)}`;
}

/**
 * The submit door's second line — what exactly is being asked for. A leader
 * proxying for ghost seats is uploading on someone else's behalf and the copy
 * must say so, or they will reasonably assume the ask is a mistake (the same
 * confusion the backend's proxy DM copy exists to prevent).
 */
export function submitCaption(liveMatch: {
  pendingSlotCount: number;
  reason: 'own' | 'proxy' | 'both';
}): string {
  const n = liveMatch.pendingSlotCount;
  const shots = `${n} screenshot${n === 1 ? '' : 's'}`;
  if (liveMatch.reason === 'proxy') return `${shots} still needed for your ghost seats`;
  if (liveMatch.reason === 'both') return `${shots} still needed — yours and your ghost seats'`;
  return `${shots} still needed`;
}

/**
 * The doors for a given screen.
 *
 * MATCH NIGHT SHORT-CIRCUITS EVERYTHING — including the sub-screens. Once a
 * window is open there is no route into VIEWING or JOINING from here at all,
 * because their entry pills are gone; anyone who was mid-flow when the window
 * opened still has their BACK button, and the dashboard reaches every one of
 * those destinations anyway.
 */
export function gateDoors(id: GateIdentity, screen: GateScreenId): GateDoor[] {
  if (id.liveMatch) {
    return [
      {
        key: 'submit',
        label: 'Submit your scores',
        to: matchCardPath(id.liveMatch),
        primary: true,
        caption: submitCaption(id.liveMatch),
      },
      { key: 'dashboard', label: 'Go to dashboard', to: DEST.dashboard },
    ];
  }

  if (screen === 'viewing') {
    return [
      { key: 'standings', label: 'Standings', to: DEST.viewStandings },
      { key: 'bracket', label: 'Bracket', to: DEST.viewBracket },
      { key: 'schedule', label: 'Schedule', to: DEST.viewSchedule },
    ];
  }

  if (screen === 'joining') {
    // "Competition as a clan" mirrors the backend's register-my-clan gate
    // exactly (leader OR founder OR team captain, clan required) — see
    // gateIdentity's canEnterComp. "Clan as a member" stays for everyone; the
    // retired BUILDING door's comp creation is absorbed here as a
    // builders-only tile (Lisa, 2026-07-21).
    return [
      ...(id.canEnterComp
        ? [{ key: 'join-comp', label: 'Competition as a clan', to: DEST.joinCompAsClan }]
        : []),
      { key: 'join-clan', label: 'Clan as a member', to: DEST.joinClanAsMember },
      ...(id.canBuildComp
        ? [{ key: 'build-comp', label: 'Build a competition', to: DEST.buildComp }]
        : []),
    ];
  }

  // ROOT, no live window — the ordinary "ARE YOU:" chooser, unchanged.
  return [
    { key: 'viewing', label: 'Viewing', screen: 'viewing', primary: !id.isMember },
    { key: 'joining', label: 'Joining', screen: 'joining' },
    // Members' clan path is the APPLICATION pill; Building was retired as a
    // root door (Lisa, 2026-07-21).
    ...(id.isOrgMember && !id.isClanLeader
      ? [{ key: 'build-clan', label: 'Apply to create a clan', to: DEST.buildClan }]
      : []),
    ...(id.isMember ? [{ key: 'dashboard', label: 'Dashboard', to: DEST.dashboard, primary: true }] : []),
    ...(id.isFounder ? [{ key: 'founder', label: 'Founder', to: DEST.founder }] : []),
  ];
}

/** The tracked header above the doors. Match night gets its own. */
export function gateHeader(id: GateIdentity, screen: GateScreenId): string {
  if (id.liveMatch) return 'MATCH NIGHT:';
  if (screen === 'viewing') return 'VIEWING:';
  if (screen === 'joining') return 'JOINING:';
  return 'ARE YOU:';
}
